import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import permissionService from '../../../services/permissionService';
import './AddEditRole.css';

const AddEditRole = () => {
  const navigate = useNavigate();
  const { roleId } = useParams();
  const isEditMode = !!roleId;

  const [formData, setFormData] = useState({
    role_id: '',
    role_name: ''
  });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEditMode);
  const [errors, setErrors] = useState({});

  const fetchRole = useCallback(async () => {
    try {
      setFetching(true);
      const response = await permissionService.getRole(roleId);

      if (response.success) {
        setFormData({
          role_id: response.data.id || '',
          role_name: response.data.role_name || ''
        });
      } else {
        toast.error(response.message || 'Failed to fetch role');
        navigate('/permission/roles');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch role';
      toast.error(errorMessage);
      navigate('/permission/roles');
    } finally {
      setFetching(false);
    }
  }, [roleId, navigate]);

  useEffect(() => {
    if (isEditMode) {
      fetchRole();
      return;
    }
    const fetchNextRoleId = async () => {
      try {
        const response = await permissionService.getNextRoleId();
        if (response.success) {
          setFormData((prev) => ({
            ...prev,
            role_id: response.data?.next_role_id || ''
          }));
        }
      } catch (err) {
        // Silent fallback: allow create without prefilled id.
      }
    };
    fetchNextRoleId();
  }, [isEditMode, fetchRole]);


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.role_name || formData.role_name.trim() === '') {
      newErrors.role_name = 'Role name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      setLoading(true);
      const data = {
        role_id: formData.role_id ? parseInt(formData.role_id, 10) : undefined,
        role_name: formData.role_name.trim()
      };

      if (isEditMode) {
        data.role_id = parseInt(roleId);
        const response = await permissionService.updateRole(data);

        if (response.success) {
          toast.success('Role updated successfully');
          navigate('/permission/roles');
        } else {
          toast.error(response.message || 'Failed to update role');
        }
      } else {
        const response = await permissionService.createRole(data);

        if (response.success) {
          toast.success('Role created successfully');
          navigate('/permission/roles');
        } else {
          toast.error(response.message || 'Failed to create role');
        }
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to save role';
      toast.error(errorMessage);

      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/permission/roles');
  };

  if (fetching) {
    return (
      <div className="add-edit-role-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading role...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="add-edit-role-page">
      <div className="role-card-container">
        <div className="card-header">
          <h2 className="card-title">{isEditMode ? 'Edit Role' : 'Add Role'}</h2>
        </div>

        <form onSubmit={handleSubmit} className="role-form">
          <div className="form-content">
            <div className="form-group">
              <label htmlFor="role_id" className="role-label">
                Role ID
              </label>
              <input
                type="text"
                id="role_id"
                name="role_id"
                value={formData.role_id}
                className="form-control"
                readOnly
                disabled
              />
            </div>
            <div className="form-group">
              <label htmlFor="role_name" className="role-label">
                Role Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="role_name"
                name="role_name"
                value={formData.role_name}
                onChange={handleChange}
                className={`form-control ${errors.role_name ? 'error' : ''}`}
                placeholder="Enter role name"
                maxLength={40}
              />
              {errors.role_name && (
                <span className="error-message-text">{errors.role_name}</span>
              )}
            </div>
          </div>

          <div className="form-actions-right">
            <button type="button" onClick={handleCancel} className="cancel-btn">
              Cancel
            </button>
            <button type="submit" className="save-btn" disabled={loading}>
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEditRole;

